import {
  RECEIVER_COUNTER_KEYS,
  STAT_KEY_INVENTORY_MAX_KEYS,
  type ReceiverCounterSummary,
  type StatKeyInventory,
} from './streamQualityContracts';

type CounterKey = keyof ReceiverCounterSummary;

/** Which inbound-rtp stat feeds each counter, and the factor that converts it to the wire unit. */
const COUNTER_SOURCES: Readonly<Record<CounterKey, { stat: string; scale: number }>> = {
  framesDecoded: { stat: 'framesDecoded', scale: 1 },
  framesDropped: { stat: 'framesDropped', scale: 1 },
  keyFramesDecoded: { stat: 'keyFramesDecoded', scale: 1 },
  nacks: { stat: 'nackCount', scale: 1 },
  plis: { stat: 'pliCount', scale: 1 },
  firs: { stat: 'firCount', scale: 1 },
  retransmittedPackets: { stat: 'retransmittedPacketsReceived', scale: 1 },
  packetsDiscarded: { stat: 'packetsDiscarded', scale: 1 },
  freezes: { stat: 'freezeCount', scale: 1 },
  freezeMs: { stat: 'totalFreezesDuration', scale: 1000 },
  pauses: { stat: 'pauseCount', scale: 1 },
};

type StatEntry = Record<string, unknown>;
type CounterSnapshot = Partial<Record<CounterKey, number>>;

function inboundKind(entry: StatEntry): string | undefined {
  if (entry['type'] !== 'inbound-rtp') return undefined;
  const kind = entry['kind'] ?? entry['mediaType'];
  return typeof kind === 'string' ? kind : undefined;
}

function sortedKeys(entry: StatEntry | undefined): string[] {
  return entry ? Object.keys(entry).sort().slice(0, STAT_KEY_INVENTORY_MAX_KEYS) : [];
}

function readCounters(entry: StatEntry): CounterSnapshot {
  const snapshot: CounterSnapshot = {};
  for (const key of RECEIVER_COUNTER_KEYS) {
    const { stat, scale } = COUNTER_SOURCES[key];
    const value = entry[stat];
    if (typeof value === 'number' && Number.isFinite(value)) snapshot[key] = value * scale;
  }
  return snapshot;
}

/**
 * Video inbound-rtp counters the ladder does not read, reported as deltas per wire send.
 *
 * Reads the report exactly as the engine returns it — no capability matrix — so the host
 * learns what Safari really emits instead of what the matrix assumes. Deltas span wire sends,
 * not polls, so a rate-limited tick rolls into the next message instead of vanishing.
 */
export class ReceiverCounterTracker {
  private videoStatId: string | undefined;
  private latest: CounterSnapshot | undefined;
  private baseline: CounterSnapshot | undefined;
  private inventory: StatKeyInventory | undefined;
  private inventoryDelivered = false;

  observe(report: RTCStatsReport): void {
    let video: StatEntry | undefined;
    let audio: StatEntry | undefined;
    report.forEach((stat) => {
      const entry = stat as StatEntry;
      const kind = inboundKind(entry);
      if (kind === 'video' && !video) video = entry;
      else if (kind === 'audio' && !audio) audio = entry;
    });
    if (!video) return;
    if (!this.inventory) this.inventory = { video: sortedKeys(video), audio: sortedKeys(audio) };

    const id = typeof video['id'] === 'string' ? video['id'] : undefined;
    if (id !== this.videoStatId) {
      // A new inbound stream restarts every counter; differencing across it would fabricate spikes.
      this.videoStatId = id;
      this.baseline = undefined;
    }
    this.latest = readCounters(video);
    if (!this.baseline) this.baseline = this.latest;
  }

  /** Deltas since the previous summary; undefined until two observations of one stream exist. */
  summarizeAndReset(): ReceiverCounterSummary | undefined {
    const { latest, baseline } = this;
    if (!latest || !baseline || latest === baseline) return undefined;
    const summary: ReceiverCounterSummary = {};
    for (const key of RECEIVER_COUNTER_KEYS) {
      const now = latest[key];
      const before = baseline[key];
      if (now === undefined || before === undefined || now < before) continue;
      summary[key] = Math.round((now - before) * 10) / 10;
    }
    this.baseline = latest;
    return Object.keys(summary).length > 0 ? summary : undefined;
  }

  /** The key inventory until a send carrying it is confirmed delivered. */
  pendingStatKeys(): StatKeyInventory | undefined {
    return this.inventoryDelivered ? undefined : this.inventory;
  }

  markStatKeysDelivered(): void {
    if (this.inventory) this.inventoryDelivered = true;
  }

  reset(): void {
    this.videoStatId = undefined;
    this.latest = undefined;
    this.baseline = undefined;
    this.inventory = undefined;
    this.inventoryDelivered = false;
  }
}
